import { deserializeGameState, type GameState } from './game'

const STORAGE_KEY = 'sun-ge-react-save-v2'
// The legacy root app is same-origin and stores its save here. Never remove
// this key: migration is a copy into the scoped React guest bucket.
const LEGACY_STORAGE_KEY = 'sun_v5_v1'
const SAVE_TIME_SUFFIX = ':saved-at'
const IDB_NAME = 'sun-ge-react'
const IDB_STORE = 'saves'

export type LocalSaveOwner = string | null | undefined
export type StoredSave = { state: GameState; savedAt: string }
const memoryFallback = new Map<string, GameState>()
const memorySaveTimes = new Map<string, string>()
const pendingWrites = new Map<string, Promise<void>>()

function ownerKey(owner: LocalSaveOwner): string {
  return owner ? `user:${encodeURIComponent(owner)}` : 'guest'
}

export function localSaveKey(owner: LocalSaveOwner = null): string {
  return `${STORAGE_KEY}:${ownerKey(owner)}`
}

export function localSaveTimeKey(owner: LocalSaveOwner = null): string {
  return `${localSaveKey(owner)}${SAVE_TIME_SUFFIX}`
}

function legacyKey(owner: LocalSaveOwner): string | null {
  return owner ? null : LEGACY_STORAGE_KEY
}

function canUseLocalStorage(): boolean {
  try {
    return typeof window !== 'undefined' && Boolean(window.localStorage)
  } catch {
    return false
  }
}

function parseSave(raw: unknown): GameState | null {
  return deserializeGameState(raw)
}

export function loadLocalSave(owner: LocalSaveOwner = null): GameState | null {
  if (canUseLocalStorage()) {
    try {
      const scopedRaw = window.localStorage.getItem(localSaveKey(owner))
      const scoped = scopedRaw ? parseSave(JSON.parse(scopedRaw)) : null
      if (scoped) return scoped

      // The old unscoped key is migrated only to the guest bucket. It is
      // intentionally never considered for a signed-in account.
      const oldKey = legacyKey(owner)
      const legacyRaw = oldKey ? window.localStorage.getItem(oldKey) : null
      const legacy = legacyRaw ? parseSave(JSON.parse(legacyRaw)) : null
      if (legacy) {
        try {
          window.localStorage.setItem(localSaveKey(owner), JSON.stringify(legacy))
          void queueIndexedDbWrite(legacy, localSaveKey(owner))
        } catch { /* best effort migration */ }
        return legacy
      }
    } catch {
      // Corrupt or unknown saves are ignored; the app can still start a run.
    }
  }
  return memoryFallback.get(ownerKey(owner)) || null
}

function validSaveTime(value: unknown): string | null {
  if (typeof value !== 'string') return null
  return Number.isFinite(Date.parse(value)) ? new Date(value).toISOString() : null
}

export function saveLocalSave(state: GameState, owner: LocalSaveOwner = null, savedAt = new Date().toISOString()): 'localStorage' | 'memory' {
  const normalizedSavedAt = validSaveTime(savedAt) || new Date().toISOString()
  memoryFallback.set(ownerKey(owner), state)
  memorySaveTimes.set(ownerKey(owner), normalizedSavedAt)
  if (canUseLocalStorage()) {
    try {
      window.localStorage.setItem(localSaveKey(owner), JSON.stringify(state))
      window.localStorage.setItem(localSaveTimeKey(owner), normalizedSavedAt)
      void queueIndexedDbWrite(state, localSaveKey(owner))
      void queueIndexedDbWrite(normalizedSavedAt, localSaveTimeKey(owner))
      return 'localStorage'
    } catch {
      // Private browsing and quota errors are expected fallback cases.
    }
  }
  void queueIndexedDbWrite(state, localSaveKey(owner))
  void queueIndexedDbWrite(normalizedSavedAt, localSaveTimeKey(owner))
  return 'memory'
}

/**
 * Persist a projection before a caller changes its synchronization marker.
 * localStorage is synchronous, while IndexedDB is deliberately queued so
 * rapid game transitions keep their order.  Conflict resolution needs the
 * stronger guarantee that the queued write has completed before a refresh
 * can load the selected cloud revision again.
 */
export async function persistLocalSave(state: GameState, owner: LocalSaveOwner = null, savedAt = new Date().toISOString()): Promise<'localStorage' | 'memory'> {
  const location = saveLocalSave(state, owner, savedAt)
  await Promise.all([
    pendingWrites.get(localSaveKey(owner))?.catch(() => undefined),
    pendingWrites.get(localSaveTimeKey(owner))?.catch(() => undefined)
  ])
  return location
}

export async function clearLocalSave(owner: LocalSaveOwner = null): Promise<void> {
  memoryFallback.delete(ownerKey(owner))
  memorySaveTimes.delete(ownerKey(owner))
  if (canUseLocalStorage()) {
    try {
      window.localStorage.removeItem(localSaveKey(owner))
      window.localStorage.removeItem(localSaveTimeKey(owner))
      const oldKey = legacyKey(owner)
      if (oldKey) window.localStorage.removeItem(oldKey)
    } catch { /* ignore */ }
  }
  await deleteIndexedDb(owner)
}

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null)
  return new Promise(resolve => {
    const request = indexedDB.open(IDB_NAME, 1)
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(IDB_STORE)) request.result.createObjectStore(IDB_STORE)
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => resolve(null)
    request.onblocked = () => resolve(null)
  })
}

async function writeIndexedDb(value: unknown, key: string): Promise<boolean> {
  const db = await openDb()
  if (!db) return false
  return new Promise(resolve => {
    const transaction = db.transaction(IDB_STORE, 'readwrite')
    transaction.objectStore(IDB_STORE).put(value, key)
    transaction.oncomplete = () => { db.close(); resolve(true) }
    transaction.onerror = () => { db.close(); resolve(false) }
    transaction.onabort = () => { db.close(); resolve(false) }
  })
}

async function readIndexedDbRaw(key: string): Promise<unknown> {
  const db = await openDb()
  if (!db) return null
  return new Promise(resolve => {
    const request = db.transaction(IDB_STORE, 'readonly').objectStore(IDB_STORE).get(key)
    request.onsuccess = () => { db.close(); resolve(request.result || null) }
    request.onerror = () => { db.close(); resolve(null) }
  })
}

async function readIndexedDbValue(key: string): Promise<GameState | null> {
  return parseSave(await readIndexedDbRaw(key))
}

export async function loadIndexedDbSave(owner: LocalSaveOwner = null): Promise<GameState | null> {
  await pendingWrites.get(localSaveKey(owner))?.catch(() => undefined)
  const scoped = await readIndexedDbValue(localSaveKey(owner))
  if (scoped) return scoped
  if (!owner) {
    const legacy = await readIndexedDbValue(LEGACY_STORAGE_KEY)
    if (legacy) {
      void queueIndexedDbWrite(legacy, localSaveKey(owner))
      return legacy
    }
  }
  return memoryFallback.get(ownerKey(owner)) || null
}

function localStorageSaveTime(owner: LocalSaveOwner): string | null {
  if (!canUseLocalStorage()) return memorySaveTimes.get(ownerKey(owner)) || null
  try {
    return validSaveTime(window.localStorage.getItem(localSaveTimeKey(owner)))
  } catch {
    return memorySaveTimes.get(ownerKey(owner)) || null
  }
}

async function indexedDbSaveTime(owner: LocalSaveOwner): Promise<string | null> {
  await pendingWrites.get(localSaveTimeKey(owner))?.catch(() => undefined)
  return validSaveTime(await readIndexedDbRaw(localSaveTimeKey(owner)))
}

export async function loadStoredSaveRecord(owner: LocalSaveOwner = null): Promise<StoredSave | null> {
  const [local, indexedDb, localTime, indexedDbTime] = await Promise.all([
    Promise.resolve(loadLocalSave(owner)),
    loadIndexedDbSave(owner),
    Promise.resolve(localStorageSaveTime(owner)),
    indexedDbSaveTime(owner)
  ])
  if (!local && !indexedDb) return null
  if (!local) return { state: indexedDb!, savedAt: indexedDbTime || new Date(0).toISOString() }
  if (!indexedDb) return { state: local, savedAt: localTime || new Date(0).toISOString() }

  const localRecord = { state: local, savedAt: localTime || new Date(0).toISOString() }
  const indexedRecord = { state: indexedDb, savedAt: indexedDbTime || new Date(0).toISOString() }
  if (indexedRecord.savedAt !== localRecord.savedAt) {
    return indexedRecord.savedAt > localRecord.savedAt ? indexedRecord : localRecord
  }
  return indexedDb.saveRevision > local.saveRevision ? indexedRecord : localRecord
}

export async function loadStoredSave(owner: LocalSaveOwner = null): Promise<GameState | null> {
  return (await loadStoredSaveRecord(owner))?.state || null
}

async function deleteIndexedDb(owner: LocalSaveOwner): Promise<void> {
  const key = localSaveKey(owner)
  await Promise.all([
    deleteIndexedDbKey(key, !owner),
    deleteIndexedDbKey(localSaveTimeKey(owner))
  ])
}

async function deleteIndexedDbKey(key: string, includeLegacy = false): Promise<void> {
  const pending = pendingWrites.get(key)
  if (pending) await pending.catch(() => undefined)
  const db = await openDb()
  if (!db) return
  await new Promise<void>(resolve => {
    const transaction = db.transaction(IDB_STORE, 'readwrite')
    const store = transaction.objectStore(IDB_STORE)
    store.delete(key)
    if (includeLegacy) store.delete(LEGACY_STORAGE_KEY)
    transaction.oncomplete = () => { db.close(); resolve() }
    transaction.onerror = () => { db.close(); resolve() }
    transaction.onabort = () => { db.close(); resolve() }
  })
}

function queueIndexedDbWrite(value: unknown, key: string): Promise<boolean> {
  const previous = pendingWrites.get(key) || Promise.resolve()
  const next = previous.catch(() => undefined).then(() => writeIndexedDb(value, key))
  const settled = next.then(() => undefined)
  pendingWrites.set(key, settled)
  void next.finally(() => {
    if (pendingWrites.get(key) === settled) pendingWrites.delete(key)
  })
  return next
}

export function saveLocationLabel(owner: LocalSaveOwner = null): string {
  if (owner) return '云端最新存档 · 离线缓存已启用'
  if (canUseLocalStorage()) return '游客存档 · 当前设备'
  if (typeof indexedDB !== 'undefined') return '游客存档 · 当前设备'
  return '游客临时存档 · 当前标签页'
}

export const guestSaveKey = localSaveKey(null)
